import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/accessibility')({
  component: AccessibilityPage,
  head: () => ({
    meta: [
      { title: 'Accessibility statement — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          'WCAG 2.1 AA accessibility statement for the Warren-Blackwood Electorate Guide: conformance, tested features, known limitations, and how to report a problem.',
      },
      { property: 'og:title', content: 'Accessibility statement — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'Our WCAG 2.1 AA accessibility commitment, what we have tested, and how to flag a problem.',
      },
    ],
  }),
});

function AccessibilityPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.4rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-lg)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
  };
  const h3: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.1rem',
    color: 'var(--green)',
    marginTop: 'var(--sp-md)',
    marginBottom: '0.4rem',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.6,
    marginBottom: 'var(--sp-sm)',
  };
  const list: React.CSSProperties = { ...p, paddingLeft: '1.2rem' };
  const li: React.CSSProperties = { marginBottom: '0.45rem' };
  const linkStyle: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Accessibility statement
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Built for everyone in the electorate
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        A civic resource is only useful if it reaches everyone it's meant for.
        This site is built to conform with the W3C's Web Content Accessibility
        Guidelines (WCAG) 2.1 at Level AA, and we'd like to hear from you when
        we fall short.
      </p>

      <h2 style={h2}>Conformance status</h2>
      <p style={p}>
        The Warren-Blackwood Electorate Guide is{' '}
        <strong>partially conformant with WCAG 2.1 Level AA</strong>. "Partially
        conformant" means most parts of the content do meet WCAG 2.1 AA, but
        some content does not yet meet the standard or has not been
        independently audited. The known gaps are listed under{' '}
        <a href="#known-limitations" style={linkStyle}>Known limitations</a>{' '}
        below.
      </p>
      <p style={p}>
        This statement applies to all pages served from{' '}
        <code>parliament-guide.lovable.app</code> and any connected custom
        domain. It does not cover third-party documents (PDFs, council
        websites) we link out to — for those, please contact the publisher
        directly.
      </p>

      <h2 style={h2}>Adjusting the site to suit you</h2>
      <h3 style={h3}>Built-in text-size toggle (A / A+ / A++)</h3>
      <p style={p}>
        The site header has a three-step text-size control. Choose{' '}
        <strong>A+</strong> for ~12% larger text, or <strong>A++</strong> for
        ~25% larger text. The choice is remembered on this device and applied
        to every page, including before the next page paints. The toggle
        scales body text, headings, button padding and line height
        proportionally — not just font size — to keep the layout legible.
      </p>
      <h3 style={h3}>Browser zoom</h3>
      <p style={p}>
        The site is built to work at up to <strong>400% browser zoom</strong>{' '}
        on a 1280×1024 viewport (WCAG 1.4.10 Reflow) without horizontal
        scrolling, loss of content, or loss of functionality.
      </p>
      <h3 style={h3}>Reduced motion</h3>
      <p style={p}>
        If your operating system has "Reduce motion" turned on, the site
        respects <code>prefers-reduced-motion</code>: map zoom transitions,
        loading spinners and reveal animations slow down or are disabled.
      </p>
      <h3 style={h3}>System dark mode</h3>
      <p style={p}>
        The site uses a single warm-cream palette with high text contrast and
        does not provide a separate dark-mode theme.
      </p>

      <h2 style={h2}>What we've tested</h2>
      <p style={p}>
        We've reviewed the site against WCAG 2.1 AA with particular attention
        to the success criteria most likely to matter for an older, often
        rural, user base:
      </p>

      <h3 style={h3}>Perceivable (WCAG Principle 1)</h3>
      <ul style={list}>
        <li style={li}>
          <strong>1.1.1 Non-text content</strong> — informational images
          carry alt text; decorative imagery is marked <code>aria-hidden</code>{' '}
          or has empty alt.
        </li>
        <li style={li}>
          <strong>1.3.1 Info and relationships</strong> — semantic HTML
          throughout (<code>&lt;main&gt;</code>, <code>&lt;nav&gt;</code>,
          <code> &lt;section&gt;</code>, headings, lists, labelled form
          controls).
        </li>
        <li style={li}>
          <strong>1.4.3 Contrast (Minimum)</strong> — body text is dark green
          or near-black ink on a warm-cream background (contrast ratio 9:1
          and above), well over the 4.5:1 AA threshold for normal text and
          3:1 for large text.
        </li>
        <li style={li}>
          <strong>1.4.10 Reflow</strong> — pages reflow cleanly to a 320px
          viewport and at 400% browser zoom without horizontal scrolling.
        </li>
        <li style={li}>
          <strong>1.4.11 Non-text Contrast</strong> — focus rings, button
          borders and chart elements meet the 3:1 ratio against adjacent
          colours.
        </li>
        <li style={li}>
          <strong>1.4.12 Text Spacing</strong> — content remains readable
          when users override line-height, letter- and word-spacing via
          browser tools or extensions.
        </li>
      </ul>

      <h3 style={h3}>Operable (WCAG Principle 2)</h3>
      <ul style={list}>
        <li style={li}>
          <strong>2.1.1 Keyboard</strong> — every interactive shire on the
          map and every locality button is reachable via <kbd>Tab</kbd> and
          activated with <kbd>Enter</kbd> or <kbd>Space</kbd>.
        </li>
        <li style={li}>
          <strong>2.1.2 No Keyboard Trap</strong> — focus can always be
          moved out of any widget using standard keys.
        </li>
        <li style={li}>
          <strong>2.4.1 Bypass Blocks</strong> — a "Skip to main content"
          link is the first focusable element on every page.
        </li>
        <li style={li}>
          <strong>2.4.3 Focus Order</strong> — tab order follows visual order;
          no positive <code>tabindex</code> values are used.
        </li>
        <li style={li}>
          <strong>2.4.7 Focus Visible</strong> — every interactive element
          has a visible focus indicator that meets 3:1 contrast.
        </li>
        <li style={li}>
          <strong>2.5.5 Target Size (AAA, applied)</strong> — primary tap
          targets are at least 44×44 pixels on mobile, beyond the AA
          minimum.
        </li>
      </ul>

      <h3 style={h3}>Understandable (WCAG Principle 3)</h3>
      <ul style={list}>
        <li style={li}>
          <strong>3.1.1 Language of Page</strong> — <code>&lt;html lang="en"&gt;</code>{' '}
          is set sitewide.
        </li>
        <li style={li}>
          <strong>3.2.3 Consistent Navigation</strong> — the header, footer
          and mobile bottom nav appear in the same place on every page.
        </li>
        <li style={li}>
          <strong>3.3.2 Labels or Instructions</strong> — every form input
          has a visible label or an <code>aria-label</code>.
        </li>
      </ul>

      <h3 style={h3}>Robust (WCAG Principle 4)</h3>
      <ul style={list}>
        <li style={li}>
          <strong>4.1.2 Name, Role, Value</strong> — the text-size toggle,
          map regions and reveal cards expose <code>role</code> and{' '}
          <code>aria-pressed</code> / <code>aria-expanded</code> state to
          assistive technology.
        </li>
        <li style={li}>
          <strong>4.1.3 Status Messages</strong> — loading and result regions
          use <code>aria-live</code> so updates are announced without moving
          focus.
        </li>
      </ul>

      <h3 style={h3}>Charts and the interactive map</h3>
      <p style={p}>
        Every chart is paired with a screen-reader-only data table so the
        underlying numbers are accessible without the visual. Map regions
        carry descriptive ARIA labels (for example,{' '}
        <em>"Shire of Manjimup. Press Enter to zoom in."</em>) and the
        keyboard alternative — searching or selecting a shire from a list —
        is offered alongside.
      </p>

      <h2 id="known-limitations" style={h2}>Known limitations</h2>
      <p style={p}>
        We are honest about where we have not yet met the bar:
      </p>
      <ul style={list}>
        <li style={li}>
          <strong>No third-party audit yet.</strong> Testing has been done
          in-house using axe DevTools, keyboard-only walkthroughs and VoiceOver
          (macOS / iOS) and NVDA (Windows). A formal third-party WCAG audit
          is on the roadmap.
        </li>
        <li style={li}>
          <strong>Linked PDFs from external sites</strong> (council
          documents, federal-agency forms) have not been tested for
          accessibility and may not meet WCAG 2.1 AA.
        </li>
        <li style={li}>
          <strong>Translations into languages other than English</strong>{' '}
          are not provided. Browser auto-translate (Chrome, Edge, Safari) is
          supported and works for most pages.
        </li>
        <li style={li}>
          <strong>The Mobile Service Locator map</strong> in the live
          DFES/Emergency WA embed is a third-party widget; its keyboard
          behaviour depends on the upstream provider.
        </li>
      </ul>

      <h2 style={h2}>Testing tools used</h2>
      <ul style={list}>
        <li style={li}>
          <a href="https://www.deque.com/axe/devtools/" style={linkStyle}>
            axe DevTools
          </a>{' '}
          (browser extension) for automated WCAG 2.1 AA checks
        </li>
        <li style={li}>
          <a href="https://wave.webaim.org/" style={linkStyle}>WAVE</a> for
          contrast and landmark structure
        </li>
        <li style={li}>
          Apple <strong>VoiceOver</strong> (macOS Sonoma, iOS 17), Microsoft{' '}
          <strong>NVDA 2024.x</strong> with Firefox, and Google Chrome
          DevTools Lighthouse accessibility audit
        </li>
        <li style={li}>
          Manual keyboard-only testing on every page, and Tab-order checks
          at 200% and 400% browser zoom
        </li>
      </ul>

      <h2 style={h2}>Report a problem</h2>
      <p style={p}>
        If something on this site isn't working with your assistive
        technology — or if any content is unclear, unreadable, or impossible
        to reach — please let us know. We'll treat accessibility bugs as a
        priority and aim to respond within five business days.
      </p>
      <p style={p}>
        Email{' '}
        <a
          href="mailto:bevan.eatts@mp.wa.gov.au?subject=Warren-Blackwood%20guide%20%E2%80%94%20accessibility%20issue"
          style={linkStyle}
        >
          bevan.eatts@mp.wa.gov.au
        </a>{' '}
        with the page URL, what you were trying to do, and (if you can) which
        browser and assistive technology you're using. If email isn't
        accessible to you, phone the electorate office on{' '}
        <a href="tel:+61898483171" style={linkStyle}>
          (08) 9848 3171
        </a>{' '}
        — see the{' '}
        <Link to="/contact" style={linkStyle}>contact page</Link> for office
        hours.
      </p>
      <p style={p}>
        If you've raised an accessibility issue and aren't satisfied with our
        response, you can escalate to the{' '}
        <a href="https://humanrights.gov.au/complaints" style={linkStyle}>
          Australian Human Rights Commission
        </a>{' '}
        — accessibility of online services is covered by the Disability
        Discrimination Act 1992 (Cth).
      </p>

      <p
        style={{
          marginTop: 'var(--sp-lg)',
          fontSize: '0.82rem',
          color: 'var(--ink-light)',
          borderTop: '1px solid var(--rule)',
          paddingTop: 'var(--sp-sm)',
          lineHeight: 1.5,
        }}
      >
        Statement prepared June 2026. Standard: WCAG 2.1 Level AA. This
        statement is maintained by the volunteer editors of the
        Warren-Blackwood Electorate Guide and reviewed at least every six
        months.
      </p>
    </main>
  );
}
